import { useEffect, useCallback, type ReactNode } from "react";
import { useSetAtom } from "jotai";
import { User } from "oidc-client-ts";
import { getUserManager } from "./oidcConfig";
import { setSessionAtom, authLoadingAtom, type SessionData } from "../store/atoms/authAtoms";

/**
 * Maps Keycloak OIDC user profile to the Portal session data format.
 */
function mapUserToSession(user: User): SessionData {
  const profile = user.profile;
  return {
    userId: profile.sub,
    email: profile.email ?? "",
    firstName: profile.given_name ?? "",
    lastName: profile.family_name ?? "",
    fullName: profile.name ?? `${profile.given_name ?? ""} ${profile.family_name ?? ""}`.trim(),
    upeId: "",
    upeName: "",
    role: "",
    preferredLanguage: ((profile as Record<string, unknown>).preferred_language as string) ?? "en",
    mfaEnabled: false,
    expiresAt: user.expires_at ? new Date(user.expires_at * 1000).toISOString() : null,
  };
}

interface AuthProviderProps {
  children: ReactNode;
}

/**
 * Initialises OIDC state on mount. Sets authLoadingAtom while resolving
 * the current user so ProtectedRoute does not redirect to /login before
 * the OIDC session has been checked.
 *
 * Skips initialisation on the /auth/callback path — AuthCallback handles
 * that route and will trigger the userLoaded event once the code exchange
 * completes.
 */
export function AuthProvider({ children }: AuthProviderProps) {
  const setSession = useSetAtom(setSessionAtom);
  const setAuthLoading = useSetAtom(authLoadingAtom);

  const syncUser = useCallback(
    (user: User | null) => {
      if (user && !user.expired) {
        setSession(mapUserToSession(user));
      } else {
        setSession(null);
      }
    },
    [setSession],
  );

  useEffect(() => {
    const mgr = getUserManager();

    // On the callback URL, AuthCallback will handle signinRedirectCallback.
    // Do not call getUser here — the code exchange hasn't happened yet so
    // getUser returns null, which clears the loading gate and causes
    // ProtectedRoute to redirect to /login before the callback completes.
    const isCallback = window.location.pathname === "/auth/callback";

    if (!isCallback) {
      setAuthLoading(true);
      mgr
        .getUser()
        .then(syncUser)
        .catch(() => syncUser(null))
        .finally(() => setAuthLoading(false));
    }

    // Listen for OIDC events — userLoaded fires after signinRedirectCallback
    // completes, which is how the callback path sets the session.
    const onLoaded = (user: User) => {
      syncUser(user);
      setAuthLoading(false);
    };
    const onUnloaded = () => setSession(null);
    const onExpired = () => setSession(null);
    const onSilentRenewError = (error: Error) => {
      console.warn("Silent renew failed, redirecting to login:", error.message);
      mgr.signinRedirect();
    };

    mgr.events.addUserLoaded(onLoaded);
    mgr.events.addUserUnloaded(onUnloaded);
    mgr.events.addSilentRenewError(onSilentRenewError);
    mgr.events.addAccessTokenExpired(onExpired);

    return () => {
      mgr.events.removeUserLoaded(onLoaded);
      mgr.events.removeUserUnloaded(onUnloaded);
      mgr.events.removeSilentRenewError(onSilentRenewError);
      mgr.events.removeAccessTokenExpired(onExpired);
    };
  }, [setSession, setAuthLoading, syncUser]);

  return <>{children}</>;
}
